import { useEffect, useRef } from "react";
import { useSubscription } from "urql";
import { graphql } from "@acto/gql";
import { NoteTodoEventType } from "@acto/gql/graphql";
import { Todo } from "@acto/taskpane/core/domain";

const NoteTodoChangedSubscription = graphql(`
  subscription OnNoteTodoChanged($noteId: ID!) {
    noteTodoChanged(noteId: $noteId) {
      __typename
      eventType
      todo {
        __typename
        id
        title
        description
        dueDate
        processedStatus
        processedReason
        isWorkflow
        isNoteBased
        assignedUser {
          id
          name
          __typename
          employee {
            id
            name
            __typename
          }
        }
        client {
          __typename
          id
          legacyId
          name
          externalReference
        }
      }
    }
  }
`);

interface UseNoteTodoSubscriptionParams {
  noteId: string;
  enabled?: boolean;
  onInitialSync?: () => void;
  onTodoCreated?: (todo: Todo) => void;
  onTodoUpdated?: (todo: Todo) => void;
  onTodoDeleted?: (todoId: string) => void;
}

export function useNoteTodoSubscription({
  noteId,
  enabled = true,
  onInitialSync,
  onTodoCreated,
  onTodoUpdated,
  onTodoDeleted,
}: UseNoteTodoSubscriptionParams) {
  const [result] = useSubscription({
    query: NoteTodoChangedSubscription,
    variables: { noteId },
    pause: !enabled || !noteId,
  });

  const initialSyncFiredRef = useRef(false);

  useEffect(() => {
    initialSyncFiredRef.current = false;
  }, [noteId]);

  useEffect(() => {
    const event = result.data?.noteTodoChanged;
    if (!event) return;

    const todo = Todo.fromGraphql(event.todo);

    switch (event.eventType) {
      case NoteTodoEventType.Initial:
        if (!initialSyncFiredRef.current) {
          initialSyncFiredRef.current = true;
          onInitialSync?.();
        }
        break;
      case NoteTodoEventType.Created:
        onTodoCreated?.(todo);
        break;
      case NoteTodoEventType.Updated:
        onTodoUpdated?.(todo);
        break;
      case NoteTodoEventType.Deleted:
        onTodoDeleted?.(event.todo.id);
        break;
    }
  }, [result.data, onInitialSync, onTodoCreated, onTodoUpdated, onTodoDeleted]);

  return {
    data: result.data?.noteTodoChanged?.todo ? Todo.fromGraphql(result.data.noteTodoChanged.todo) : null,
    eventType: result.data?.noteTodoChanged?.eventType,
    error: result.error,
    fetching: result.fetching,
  };
}
